import { PasswordUtil } from "@dto/util/PasswordUtil";
import { Property } from 'csstype';
import { ReactElement } from "react";
import { Log } from 'src/service/log.service';
import { renderToMeasureHTML } from './renderStatic';

export abstract class TextUtil {

  /**
   * {@link JSON.stringify} with default spacing=2, and {@link TextUtil.hideKeysReplacer} applied
   * @param json A JavaScript value, usually an object or array, to be converted
   * @param replacer An array of strings and numbers that acts as an approved list for selecting the object properties that will be stringified
   * @param space Adds indentation, white space, and line break characters to the return-value JSON text to make it easier to read, default is set to 2
   * @returns formatted string
   */
  static formatJSON(json, replacer?: (string | number)[], space: string | number = TextUtil.DEFAULT_JSON_SPACE) {
    let replacedObj = json;
    if (replacer) {
      const replacedStr = JSON.stringify(json, replacer);
      replacedObj = JSON.parse(replacedStr);
    }
    return JSON.stringify(replacedObj, TextUtil.hideKeysReplacer, space);
  }

  /**
   * Indentation of {@link TextUtil.formatJSON} output
   */
  static readonly DEFAULT_JSON_SPACE = 2;
  static readonly OBJ_REGEX = /^\s*\{.*\}\s*$/;
  static readonly VALUE_REMOVED = "-=removed=-";
  /**
   * Replacer for {@link JSON.stringify} hiding password like key values
   * @param key
   * @param value
   * @returns
   */
  static hideKeysReplacer(key: string, value: unknown) {
    const lowerKey = key.toLowerCase();
    if (PasswordUtil.KEYS_TO_HIDE.some(k => lowerKey.includes(k.toLowerCase()))) {
      return TextUtil.VALUE_REMOVED;
    }
    if (typeof value === 'string' && TextUtil.OBJ_REGEX.test(value)) {
      try {
        const valObj = JSON.parse(value);
        return JSON.stringify(valObj, TextUtil.hideKeysReplacer);
      } catch (e) {
        Log.debug('Failed to parse JSON like string', e);
        return value;
      }
    }
    return value;
  }

  /**
   * Default max length of text to keep untrimmed
   */
  static readonly DEFAULT_TRIM_LENGTH = 32;
  /**
   * Marks the cut in trimmed text
   */
  static readonly ELLIPSIS = '…';

  /**
   * Returns text shorten to maxLength characters with '...' in middle
   * @param text
   * @param maxLength default {@link TextUtil.DEFAULT_TRIM_LENGTH} chars
   * @returns
   */
  static trimMiddle<T>(text: T, maxLength = TextUtil.DEFAULT_TRIM_LENGTH) {
    if (typeof text !== 'string' || text?.length < maxLength || maxLength < 3) return text;
    const halfMaxLength = Math.floor((maxLength - 1) / 2);
    return text.substring(0, halfMaxLength) + TextUtil.ELLIPSIS + text.substring(text.length - halfMaxLength);
  }

  /**
   * Returns text shorten to maxLength characters with '...' at end
   * @param text string to trim
   * @param maxLength default is to keep 32 characters
   * @returns
   */
  static trimEnd<T>(text: T, maxLength = TextUtil.DEFAULT_TRIM_LENGTH) {
    if (typeof text !== 'string' || text?.length < maxLength || maxLength < 3) return text;
    return text.substring(0, maxLength) + TextUtil.ELLIPSIS;
  }

  /**
   * Capitalize first letter, keep rest as is
   * @param text
   */
  static capitalize(text: string) {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /**
   * Returns width of text
   * @param text text to measure
   * @param width initial CSS width of container, null=auto
   * @param refEl element whose CSS style to use
   * @returns
   */
  static measureText(text: React.ReactNode, width?: Property.Width, refEl?: HTMLElement) {
    if (!text) return;
    const style = getComputedStyle(refEl ?? document.body);
    const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const div = document.body.querySelector<HTMLElement>('div#textMeasure');
    if (typeof text === 'object') {
      // const root = createRoot(div);
      // flushSync(() => root.render(text));

      div.innerHTML = renderToMeasureHTML(text as ReactElement);
    }
    else div.textContent = String(text);
    div.style.width = String(width || 'auto');
    div.style.font = font;
    return div.clientWidth;
  }

  /**
   * Returns text without whitespace characters
   * @param text to replace
   * @returns replaced text
   */
  static removeWhitespace(text: string) {
    return text?.replace(/\s+/g, '');
  }

  /**
   * Matches text against a filter term honoring case-sensitive and regex modes.
   * Mirrors the default Grid string filter so custom {@link Grid} onFilter callbacks
   * behave consistently with built-in columns.
   * @param text value to test
   * @param term filter term
   * @param isCaseSensitive when true, plain substring match keeps case
   * @param isRegex when true, term is treated as a regular expression
   * @returns whether text matches the term
   */
  static matchFilter(text: string, term: string, isCaseSensitive?: boolean, isRegex?: boolean) {
    const value = text ?? '';
    if (isRegex)
      return new RegExp(term).test(value);
    if (isCaseSensitive)
      return value.includes(term);
    return value.toLowerCase().includes(term.toLowerCase());
  }

  /** Collapse runs of whitespace to a single space, matching how the browser
   *  renders cells with white-space: normal/nowrap. Use before canvas measuring. */
  static collapseWhitespace(text: string) {
    return text?.replace(/\s+/g, ' ').trim();
  }

}